import React, { useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  GroupWork as GroupIcon,
  CallSplit as UngroupIcon,
  ContentCopy as DuplicateIcon,
  ArrowBack as ArrowBackIcon,
  Keyboard as KeyboardIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  AutoFixHigh as AutoFixHighIcon,
  Remove as MinusIcon,
  Add as PlusIcon
} from '@mui/icons-material';
import InstationLogo from '../layout/Instation-complete.svg';

const CANVAS_SIZES = [
  { id: 'portrait', name: 'LinkedIn Portrait', width: 1080, height: 1350 },
  { id: 'square', name: 'LinkedIn Square', width: 1080, height: 1080 }
];

const CarouselToolbar = ({
  // Document props
  documentName,
  setDocumentName,
  canvasSize,
  handleCanvasSizeChange,
  

  
  // History props
  historyIndex,
  history,
  undo,
  redo,
  
  // Selection props
  selectedObject,
  fabricCanvasRef,
  
  // Actions
  duplicateSelected,
  groupSelected,
  ungroupSelected,
  deleteSelected,
  
  // View props
  showGrid,
  setShowGrid,
  zoom,
  setZoom,
  zoomLocked,
  toggleZoomLock,
  
  // Export props
  downloadImage,
  exportToPDF,
  
  // Navigation
  handleBackToTemplates,
  
  // Dialogs
  setShowShortcuts,
  
  // Template saving
  onSaveTemplate,
  
  // AI Design
  setAiMenuAnchorEl
}) => {
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  const handleZoomIn = () => {
    setZoom(Number((Math.min(1.5, zoom + 0.1)).toFixed(2)));
  };

  const handleZoomOut = () => {
    setZoom(Number((Math.max(0.1, zoom - 0.1)).toFixed(2)));
  };

  const handleResetZoom = () => {
    setZoom(1.0);
  };

  const handleExportMenuClick = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportPNG = () => {
    downloadImage('png');
    handleExportMenuClose();
  };

  const handleExportPDF = () => {
    exportToPDF();
    handleExportMenuClose();
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      height: 60,
      px: 3,
      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
      bgcolor: 'white',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Logo Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
        <a href="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
          <img src={InstationLogo} alt="Instation" style={{ height: 32, width: 'auto' }} />
        </a>
        <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 32 }} />
        <Typography variant="h6" sx={{ 
          fontWeight: 700, 
          color: '#1e293b',
          letterSpacing: '-0.5px',
          fontSize: '1.1rem'
        }}>
          Carousel Designer
        </Typography>
        <Button
          onClick={handleBackToTemplates}
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          size="small"
          sx={{ 
            ml: 2,
            textTransform: 'none',
            borderColor: '#e2e8f0',
            color: '#64748b',
            py: 0.5,
            '&:hover': {
              borderColor: '#cbd5e1',
              bgcolor: '#f8fafc'
            }
          }}
        >
          Back
        </Button>
      </Box>
      
      {/* Document Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
        <TextField
          value={documentName}
          onChange={(e) => setDocumentName(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="Carousel name..."
          sx={{ 
            minWidth: 220,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
              '&.Mui-focused': { bgcolor: 'white' }
            }
          }}
        />
        <FormControl size="small" sx={{ ml: 2, minWidth: 140 }}>
          <InputLabel id="canvas-size-label">Canvas Size</InputLabel>
          <Select
            labelId="canvas-size-label"
            value={canvasSize.id}
            label="Canvas Size"
            renderValue={(value) => {
              const size = CANVAS_SIZES.find(s => s.id === value);
              return size ? size.name : '';
            }}
            onChange={(e) => {
              const selectedSize = CANVAS_SIZES.find(size => size.id === e.target.value);
              if (selectedSize) {
                handleCanvasSizeChange(selectedSize);
              }
            }}
            sx={{ 
              bgcolor: '#f8fafc',
              '&:hover': { bgcolor: '#f1f5f9' },
              '&.Mui-focused': { bgcolor: 'white' }
            }}
          >
            {CANVAS_SIZES.map((size) => (
              <MenuItem key={size.id} value={size.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {size.name}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {size.width}×{size.height}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      

      
      {/* Edit Actions */}
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mr: 3 }}>
        <Tooltip title="Undo (Ctrl+Z)" arrow>
          <span>
            <IconButton 
              onClick={undo} 
              disabled={historyIndex <= 0} 
              size="small"
              sx={{
                bgcolor: historyIndex > 0 ? 'rgba(156, 163, 175, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(156, 163, 175, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <UndoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo (Ctrl+Y)" arrow>
          <span>
            <IconButton 
              onClick={redo} 
              disabled={historyIndex >= history.length - 1} 
              size="small"
              sx={{
                bgcolor: historyIndex < history.length - 1 ? 'rgba(156, 163, 175, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(156, 163, 175, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <RedoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 24 }} />
        
        <Tooltip title="Duplicate (Ctrl+D)" arrow>
          <span>
            <IconButton 
              onClick={duplicateSelected} 
              disabled={!selectedObject} 
              size="small"
              sx={{
                bgcolor: selectedObject ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <DuplicateIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Group (Ctrl+G)" arrow>
          <span>
            <IconButton 
              onClick={groupSelected} 
              disabled={!selectedObject || !fabricCanvasRef.current?.getActiveObjects()?.length || fabricCanvasRef.current.getActiveObjects().length <= 1} 
              size="small"
              sx={{
                bgcolor: selectedObject && fabricCanvasRef.current?.getActiveObjects()?.length > 1 ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(59, 130, 246, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <GroupIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ungroup (Ctrl+Shift+G)" arrow>
          <span>
            <IconButton 
              onClick={ungroupSelected} 
              disabled={!selectedObject || (selectedObject && selectedObject.type !== 'group')} 
              size="small"
              sx={{
                bgcolor: selectedObject && selectedObject.type === 'group' ? 'rgba(168, 85, 247, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <UngroupIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Delete (Del)" arrow>
          <span>
            <IconButton 
              onClick={deleteSelected} 
              disabled={!selectedObject} 
              size="small"
              sx={{
                bgcolor: selectedObject ? 'rgba(239, 68, 68, 0.1)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' },
                '&:disabled': { bgcolor: 'transparent' },
                borderRadius: 2
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
      
      {/* View Controls */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mr: 3 }}>
        <FormControlLabel
          control={
            <Switch 
              checked={showGrid} 
              onChange={(e) => setShowGrid(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#4890fe',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#4890fe',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#64748b' }}>
              Grid
            </Typography>
          }
        />
        
        {/* Zoom Controls */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          backgroundColor: 'rgba(226, 232, 240, 0.5)',
          borderRadius: '8px',
          padding: '4px 8px'
        }}>
          <Tooltip title="Zoom out">
            <IconButton
              size="small"
              onClick={handleZoomOut}
              disabled={zoom <= 0.1}
              sx={{
                color: '#4a5568',
                '&:hover': {
                  backgroundColor: 'rgba(74, 85, 104, 0.08)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(74, 85, 104, 0.3)'
                }
              }}
            >
              <MinusIcon />
            </IconButton>
          </Tooltip>
          
          <Typography 
            variant="body2" 
            sx={{ 
              width: 60, 
              textAlign: 'center',
              fontWeight: 600,
              color: '#4a5568',
              cursor: 'pointer'
            }}
            onClick={handleResetZoom}
          >
            {Math.round(zoom * 100)}%
          </Typography>
          
          <Tooltip title="Zoom in">
            <IconButton
              size="small"
              onClick={handleZoomIn}
              disabled={zoom >= 1.5}
              sx={{
                color: '#4a5568',
                '&:hover': {
                  backgroundColor: 'rgba(74, 85, 104, 0.08)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(74, 85, 104, 0.3)'
                }
              }}
            >
              <PlusIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        <Tooltip title={zoomLocked ? "Unlock zoom" : "Lock zoom"} arrow>
          <IconButton
            onClick={toggleZoomLock}
            size="small"
            sx={{
              color: zoomLocked ? '#ef4444' : '#64748b',
              border: '1px solid',
              borderColor: zoomLocked ? '#ef4444' : '#d1d5db',
              borderRadius: 2,
              '&:hover': {
                bgcolor: zoomLocked ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)',
                borderColor: zoomLocked ? '#dc2626' : '#9ca3af'
              }
            }}
          >
            {zoomLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <LockOpenIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Stack>
      
      <Box sx={{ flexGrow: 1 }} />
      
      {/* Export Actions */}
      <Stack direction="row" spacing={1}>
        <Tooltip title="Keyboard Shortcuts (?)" arrow>
          <IconButton
            onClick={() => setShowShortcuts(true)}
            size="small"
            sx={{ 
              borderColor: '#d1d5db',
              color: '#64748b',
              border: '1px solid #d1d5db',
              '&:hover': { 
                borderColor: '#9ca3af',
                bgcolor: 'rgba(0, 0, 0, 0.02)'
              },
              borderRadius: 2,
              mr: 1
            }}
          >
            <KeyboardIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        {/* AI Design Button */}
        <Tooltip title="AI Design Generator" arrow>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={(event) => setAiMenuAnchorEl(event.currentTarget)}
            sx={{ 
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              '&:hover': {
                borderColor: '#7c3aed',
                bgcolor: 'rgba(139, 92, 246, 0.04)'
              },
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 500,
              px: 2,
              py: 0.5,
              fontSize: '0.875rem',
              mr: 1
            }}
          >
            AI Design
          </Button>
        </Tooltip>
        
        {/* Save as Template Button */}
        {onSaveTemplate && (
          <Tooltip title="Save as Template" arrow>
            <Button
              variant="outlined"
              size="small"
              onClick={onSaveTemplate}
              sx={{ 
                borderColor: '#16a34a',
                color: '#16a34a',
                '&:hover': {
                  borderColor: '#15803d',
                  bgcolor: 'rgba(22, 163, 74, 0.04)'
                },
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 500,
                px: 2,
                py: 0.5,
                fontSize: '0.875rem',
                mr: 1
              }}
            >
              Save Template
            </Button>
          </Tooltip>
        )}
        
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          endIcon={<ArrowDownIcon />}
          onClick={handleExportMenuClick}
          sx={{ 
            bgcolor: '#4338ca',
            fontWeight: 500,
            px: 2,
            py: 0.5,
            fontSize: '0.875rem',
            borderRadius: 1.5,
            textTransform: 'none',
            boxShadow: '0 2px 6px rgba(67, 56, 202, 0.2)',
            '&:hover': {
              bgcolor: '#3730a3',
              boxShadow: '0 3px 8px rgba(67, 56, 202, 0.3)',
            },
            transition: 'all 0.2s ease-in-out'
          }}
        >
          Export
        </Button>

        <Menu
          anchorEl={exportMenuAnchor}
          open={exportMenuOpen}
          onClose={handleExportMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          sx={{
            '& .MuiPaper-root': {
              minWidth: 160,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <MenuItem onClick={handleExportPNG} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <ImageIcon sx={{ fontSize: 20, color: '#059669' }} />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Export as PNG
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                High quality images
              </Typography>
            </ListItemText>
          </MenuItem>
          <MenuItem onClick={handleExportPDF} sx={{ py: 1.5 }}>
            <ListItemIcon>
              <PdfIcon sx={{ fontSize: 20, color: '#dc2626' }} />
            </ListItemIcon>
            <ListItemText>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Export as PDF
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Print-ready document
              </Typography>
            </ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    </Box>
  );
};

export default CarouselToolbar; 